import React, { useContext, useState } from 'react';
import { styled } from '@this/constants/themes';

import FlightSeatMapModal from '@this/components/reserve_trip/reserve_confirm/flight_seat_map_modal';
import { Button } from '@material-ui/core';
import Transit from '@this/domain/transit/transit2';
import Flight from '@this/domain/flight/flight2';
import Hotel from '@this/domain/hotel/hotel2';
import MarketLogHelper from '@this/domain/market_log/market_log_helper';
import { Text } from '@this/components/shared/ui/data_displays/typography';

import { Flex } from '@this/shared/ui/layout/flex';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import ExternalLinkIcon from '@this/src/components/shared/icons/external_link_icon';
import type ReservingTripItem from '@this/src/domain/trip/reserving_trip_item2';
import CoreAppContext from '@this/src/components/core_app/core_app_context';
import { observer } from 'mobx-react';
import { digits } from '@this/src/util';
import ReserveConfirmTransitTr from '../tripelement_tr/transit_tr';
import ReserveConfirmHotelTr from '../tripelement_tr/hotel_tr';
import ReserveConfirmFlightTr from '../tripelement_tr/flight_tr';

type Props = {
  isPackage: boolean;
  domesticAirPriceIndex: number | null | undefined;
  item: ReservingTripItem;
};

const ReserveConfirmOutlineItemTemplate: React.FC<Props> = ({ isPackage, domesticAirPriceIndex, item }) => {
  const { serviceId } = useContext(CoreAppContext);
  const [showFlightSeatMpaModal, setShowFlightSeatMapModal] = useState(false);
  const itemTestIdPrefix = `reserve-confirm-summary-item-${item.index}`;

  return (
    <ReserveConfirmRightSection data-testid="reserve-confirm-summary-item" id={itemTestIdPrefix}>
      {item.elementType === 'transport' && item.element instanceof Transit ? (
        <>
          <ReserveConfirmRightTitle>
            <p data-testid={`${itemTestIdPrefix}-type`}>{item.element.reserveConfirmElementLabel}</p>
            <p>
              {!item.element.package && (
                <>
                  <span data-wovn-ignore data-testid={`${itemTestIdPrefix}-price`}>
                    {digits(item.element.reserveConfirmElementPrice)}
                  </span>
                  円
                </>
              )}
            </p>
          </ReserveConfirmRightTitle>
          <ReserveConfirmRightContent>
            <ReserveConfirmTransitTr
              itemTestIdPrefix={itemTestIdPrefix}
              transit={item.element}
              domesticAirPriceIndex={domesticAirPriceIndex}
            />
            {MarketLogHelper.isObjectMarketLogLinkAvailable2(item.element, serviceId) && (
              <Link
                href={MarketLogHelper.objectMarketLogPath2(item.element, domesticAirPriceIndex)}
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <div>マーケットログを確認</div>
                <div style={{ marginTop: '2px', marginLeft: '2px' }}>
                  <ExternalLinkIcon />
                </div>
              </Link>
            )}
          </ReserveConfirmRightContent>
        </>
      ) : item.elementType === 'flight' && item.element instanceof Flight ? (
        <>
          <ReserveConfirmRightTitle>
            <p data-testid={`${itemTestIdPrefix}-type`}>飛行機</p>
            <p>
              <span data-wovn-ignore data-testid={`${itemTestIdPrefix}-price`}>
                {digits(item.element.price)}
              </span>
              円
            </p>
          </ReserveConfirmRightTitle>
          <ReserveConfirmRightContent>
            <Flex alignItems="center">
              <Text color="description" level="caption">
                発券期限：
              </Text>
              <Text color="danger" level="caption" bold>
                {item.element.itineraries.length > 0 && item.element.itineraries[0].rawTicketExpiredAt ? (
                  <span data-wovn-ignore>
                    {item.element.itineraries[0].rawTicketExpiredAt.format('YYYY/MM/DD HH:mm')}
                  </span>
                ) : (
                  <span data-wovn-ignore>ご依頼から{item.deadlineHour}時間</span>
                )}
              </Text>
            </Flex>
            <br />
            <ReserveConfirmFlightTr flight={item.element} />
            {item.foreignExist && MarketLogHelper.isObjectMarketLogLinkAvailable2(item.element, serviceId) && (
              <Link
                href={MarketLogHelper.objectMarketLogPath2(item.element, item.domesticAirPriceIndex)}
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <div>マーケットログを確認</div>
                <div style={{ marginTop: '2px', marginLeft: '2px' }}>
                  <ExternalLinkIcon />
                </div>
              </Link>
            )}
            {item.element.seatReservationEnable && (
              <Button
                color="primary"
                onClick={() => {
                  setShowFlightSeatMapModal(true);
                }}
              >
                座席指定する
              </Button>
            )}
            <FlightSeatMapModal
              show={showFlightSeatMpaModal}
              onHide={() => {
                setShowFlightSeatMapModal(false);
              }}
              flight={item.element}
              travelers={item.parentTrip?.travelers ?? []}
            />
          </ReserveConfirmRightContent>
        </>
      ) : item.elementType === 'hotel' && item.element instanceof Hotel ? (
        <>
          <ReserveConfirmRightTitle>
            <p data-testid={`${itemTestIdPrefix}-type`}>宿泊</p>
            <p>
              <span data-wovn-ignore data-testid={`${itemTestIdPrefix}-price`}>
                {digits(item.element.price || 0)}
              </span>
              円
            </p>
          </ReserveConfirmRightTitle>
          <ReserveConfirmRightContent>
            <ReserveConfirmHotelTr
              hotel={item.element}
              isPackage={isPackage}
              hotelPrice={item.element.price || 0}
              itemTestIdPrefix={itemTestIdPrefix}
              peoplenum={item.element.peopleNum}
              roomnum={item.element.roomnum}
            />
            {!isPackage && (
              <div>
                <Link
                  href={item.element.detailPath || undefined}
                  isExternal
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  詳細を確認
                </Link>
              </div>
            )}
            {MarketLogHelper.isObjectMarketLogLinkAvailable2(item.element, serviceId) && (
              <Link
                href={MarketLogHelper.objectMarketLogPath2(item.element)}
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <div>マーケットログを確認</div>
                <div style={{ marginTop: '2px', marginLeft: '2px' }}>
                  <ExternalLinkIcon />
                </div>
              </Link>
            )}
          </ReserveConfirmRightContent>
        </>
      ) : null}
    </ReserveConfirmRightSection>
  );
};

const ReserveConfirmRightSection = styled.div`
  margin-bottom: 15px;
`;

const ReserveConfirmRightTitle = styled.div`
  font-weight: bold;
  color: #959595;
  padding: 10px 10px 0 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReserveConfirmRightContent = styled.div`
  font-weight: bold;
  color: #959595;
  padding: 6px 10px 10px 10px;
`;

export default observer(ReserveConfirmOutlineItemTemplate);
